import React, { useEffect, useState } from 'react';
import { useParams, Link  } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChevronDownIcon, NewspaperIcon, QuestionMarkCircleIcon, BookOpenIcon} from '@heroicons/react/24/outline';
import ReviewsSlider from '../components/Reviews';
import SubTopicsGrid from '../components/SubTopicsGrid';
import ContentRenderer from './admin/ContentRenderer';
import { ArrowLeft ,CheckCircle, Clock, Shield,Phone} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { FaWhatsapp ,FaWaze, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import AnimatedHeader from '../components/AnimatedHeader';
import ProcessSteps from '../components/ProcessSteps';
import HeaderLayout from '../components/HeaderLayout';
import ReasonsList from '../components/ReasonsCard';
import ResponsiveImage from '../components/ResponsiveImage';
import PowerOfAttorneySteps from '../components/PowerOfAttorneySteps';

const MAX_CONTENT_LENGTH = 200; // מספר התווים המקסימלי לתקציר
const topicIdMapping = {
  realestate: 'Cq3auS1VsmtbG10Kw2ef',
  execution: 'niwjxuloe5cfnBVAIxPr',
  damages: 'vzOfLHY5Y0lGDXpQbnie',
  powerOfAttorney: 'kuje0EhTG705PyObVt0g'
};

// פונקציה לטעינה מוקדמת של תמונות
const preloadImages = (imageUrls) => {
  imageUrls.forEach(url => {
    if (url) {
      const img = new Image();
      img.src = url;
    }
  });
};

const splitContent = (content) => {
  if (!content) return ['', ''];
  
  // אם התוכן קצר, נחזיר את כולו בחלק הראשון
  if (content.length < 200) {
    return [content, ''];
  }
  
  // נחפש כותרות מודגשות (markdown או HTML bold) כנקודות חלוקה עדיפות
  const boldHeaderPatterns = [
    /\*\*([^*]+)\*\*/g, // Markdown bold: **text**
    /<strong>([^<]+)<\/strong>/g, // HTML strong tags
    /<b>([^<]+)<\/b>/g, // HTML bold tags
  ];
  
  // מוצאים את כל הכותרות המודגשות
  const boldHeaders = [];
  
  boldHeaderPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      boldHeaders.push({
        index: match.index,
        text: match[1],
        fullMatch: match[0]
      });
    }
  });
  
  // אם מצאנו כותרות מודגשות, נחפש את הטובה ביותר לחלוקה
  if (boldHeaders.length > 0) {
    const halfLength = Math.floor(content.length / 2);
    
    // מחפשים את הכותרת הקרובה ביותר לאמצע התוכן
    let bestHeader = null;
    let minDistance = Infinity;
    
    boldHeaders.forEach(header => {
      const distance = Math.abs(header.index - halfLength);
      // מעדיפים כותרות שמופיעות אחרי 30% מהתוכן (לא בהתחלה)
      if (header.index > content.length * 0.3 && distance < minDistance) {
        minDistance = distance;
        bestHeader = header;
      }
    });
    
    // אם מצאנו כותרת מתאימה, נחלק לפניה
    if (bestHeader) {
      const splitIndex = bestHeader.index;
      const firstHalf = content.substring(0, splitIndex).trim();
      const secondHalf = content.substring(splitIndex).trim();
      
      return [firstHalf, secondHalf];
    }
  }
  
  // אם לא מצאנו כותרות מתאימות, נחזור לשיטה הקודמת
  const halfLength = Math.floor(content.length / 2);
  
  // נחפש נקודת חלוקה טובה סביב האמצע
  const searchRange = 200;
  const startSearch = Math.max(0, halfLength - searchRange);
  const endSearch = Math.min(content.length, halfLength + searchRange);
  
  // דרגי עדיפות לנקודות חלוקה:
  const breakPoints = [
    '</p>', // סוף פסקה - העדיפות הגבוהה ביותר
    '.</p>', // נקודה בסוף פסקה
    '. ', // נקודה ורווח
    '! ', // סימן קריאה ורווח
    '? ', // סימן שאלה ורווח
    ': ', // נקודתיים ורווח
    '; ', // נקודה פסיק ורווח
    ', ', // פסיק ורווח
    ' ', // רווח כאפשרות אחרונה
  ];
  
  let bestSplitIndex = halfLength;
  
  // נחפש את נקודת החלוקה הטובה ביותר
  for (const breakPoint of breakPoints) {
    const searchText = content.substring(startSearch, endSearch);
    const breakIndex = searchText.lastIndexOf(breakPoint);
    
    if (breakIndex !== -1) {
      bestSplitIndex = startSearch + breakIndex + breakPoint.length;
      break;
    }
  }
  
  // אם לא מצאנו נקודת חלוקה טובה, נחפש לפחות רווח
  if (bestSplitIndex === halfLength) {
    const beforeHalf = content.substring(0, halfLength);
    const afterHalf = content.substring(halfLength);
    
    const lastSpaceBefore = beforeHalf.lastIndexOf(' ');
    const firstSpaceAfter = afterHalf.indexOf(' ');
    
    if (lastSpaceBefore !== -1 && (firstSpaceAfter === -1 || (halfLength - lastSpaceBefore) <= firstSpaceAfter)) {
      bestSplitIndex = lastSpaceBefore + 1;
    } else if (firstSpaceAfter !== -1) {
      bestSplitIndex = halfLength + firstSpaceAfter + 1;
    }
  }
  
  const firstHalf = content.substring(0, bestSplitIndex).trim();
  const secondHalf = content.substring(bestSplitIndex).trim();
  
  return [firstHalf, secondHalf];
};

const createSummary = (content) => {
  if (!content) return '';
  const strippedContent = content.replace(/<[^>]+>/g, '');
  if (strippedContent.length <= MAX_CONTENT_LENGTH) return content;
  return strippedContent.substring(0, MAX_CONTENT_LENGTH) + '...';
};

const SectionWithImage = ({ 
  children, 
  imageSrc, 
  imagePosition = "left",
  imageAlt = ""
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  if (isMobile) {
    // Mobile - just the component, no image
    return <div>{children}</div>;
  }

  // Desktop - split layout: 40% image, 60% component
  return (
    <div className="grid lg:grid-cols-5 min-h-screen">
      {/* Image Section - Takes 2/5 of the space (40%) */}
      <div className={`relative lg:col-span-2 ${imagePosition === 'right' ? 'order-2' : 'order-1'}`}>
        <img 
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          style={{
            // הוספת cache headers
            imageRendering: 'auto',
          }}
        />
        {/* Optional overlay for better text readability if needed */}
        <div className="absolute inset-0 bg-black/10"></div>
      </div>

      {/* Component Section - Takes 3/5 of the space (60%) */}
      <div className={`flex items-center justify-center bg-white lg:col-span-3 ${imagePosition === 'right' ? 'order-1' : 'order-2'}`}>
        <div className="w-full max-w-3xl px-8">
          {children}
        </div>
      </div>
    </div>
  );
};

const TopicPage = () => {
  const { topicId } = useParams();
  const [topicData, setTopicData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const isPowerOfAttorney = topicId === 'powerOfAttorney';
  const isExecution = topicId === 'execution';
  // פונקציה להחזרת תמונה לפי נושא
  const getTopicImage = (topicData) => {
    if (topicData && topicData.mainImage) {
      return topicData.mainImage;
    }
    return '/אראל4.jpg';
  };
  
  // פונקציה להחזרת תמונה שנייה לפי נושא
  const getTopicSecondImage = (topicData) => {
    if (topicData && topicData.secondImage) {
      return topicData.secondImage;
    }
    return '/חן4.jpg';
  };
  
  // פונקציה להחזרת תמונה שלישית
  const getTopicThirdImage = (topicData) => {
    if (topicData && topicData.thirdImage) {
      return topicData.thirdImage;
    }
    return '/אראל4.jpg';
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    // גלילה לחלק העליון של הדף
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [topicId]);

  // טעינה מוקדמת של תמונות ברגע שיש לנו topicData
  useEffect(() => {
    if (topicData) {
      const imagesToPreload = [
        getTopicImage(topicData),
        getTopicSecondImage(topicData), 
        getTopicThirdImage(topicData)
      ];
      
      // טעינה מוקדמת של תמונות המאמרים
      const articleImages = articles
        .map(article => article.imageUrl)
        .filter(Boolean);
      
      preloadImages([...imagesToPreload, ...articleImages]);
    }
  }, [topicData, articles]);
  
  useEffect(() => {
    const fetchTopicData = async () => {
      try {
        const mappedTopicId = topicIdMapping[topicId];
        if (!mappedTopicId) {
          setTopicData(null);
          setLoading(false);
          return;
        }

        const topicDoc = await getDoc(doc(db, 'subjects', mappedTopicId));
        if (topicDoc.exists()) {
          setTopicData(topicDoc.data());

          const questionsQuery = query(
            collection(db, 'questions'),
            where('subjectId', '==', mappedTopicId)
          );
          const questionsSnapshot = await getDocs(questionsQuery);
          setQuestions(
            questionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          );

          const articlesQuery = query(
            collection(db, 'articles'),
            where('subjectId', '==', mappedTopicId)
          );
          const articlesSnapshot = await getDocs(articlesQuery);
          setArticles(
            articlesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          );
        } else {
          setTopicData(null);
        }
      } catch (error) {
        console.error('Error fetching topic data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopicData();
  }, [topicId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!topicData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 bg-red-50 rounded-lg">
          <h2 className="text-2xl font-bold text-red-800">הנושא לא נמצא</h2>
          <p className="text-red-600 mt-2">נא לבדוק את הקישור ולנסות שוב</p>
        </div>
      </div>
    );
  }

  const QuestionCard = ({ question, answer, isActive, onClick }) => (
    <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden">
      <button
        onClick={onClick}
        className="w-full text-right px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1 text-right">
          <ContentRenderer content={question} />
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${
            isActive ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isActive && (
        <div className="px-6 py-4 bg-gray-50 border-t text-right dir-rtl">
          <ContentRenderer content={answer} />
        </div>
      )}
    </div>
  );

  const ArticleCard = ({ title, content, articleId, image }) => (
    <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col h-[550px]">
      {/* Article Image - גובה קבוע לתמונה */}
      <div className="w-full h-52 flex-shrink-0">
        <img
          src={image || "/articlesImages/articleImage1.jpg"}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
          fetchPriority="low"
        />
      </div>
      
      {/* Article Content */}
      <div className="flex flex-col flex-grow p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 line-clamp-2">
          <ContentRenderer content={title} />
        </h3>
        
        <div className="text-gray-600 mb-4 line-clamp-4 flex-grow">
          <ContentRenderer content={createSummary(content)} />
        </div>

        <Link 
          to={`/topic/${topicId}/${articleId}`}
          className="w-full text-center py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition-colors mt-auto"
        >
          קרא עוד
        </Link>
      </div>
    </div>
  );

  return (
    <div className="flex-grow bg-[var(--bg-primary)]">
 
    <HeaderLayout topicData={topicData} />

    <div className="w-full">
        {/* Header Section */}
        {topicId === 'execution' && (
  <div className="bg-gradient-to-br from-white via-gray-100 to-white py-16 px-6 md:px-12 max-w-6xl mx-auto rounded-3xl shadow-2xl mt-10 mb-20 border border-gray-200">
    <div className="flex flex-col md:flex-row items-center md:items-start gap-10">
      
      {/* תמונה */}
      <div className="flex-shrink-0 w-full md:w-1/3">
        <img
          src="/אראלחןחדלותמשותפת.jpg"
          alt="כתבה על משרד עורכי הדין"
          className="rounded-2xl shadow-xl w-full object-cover"
        />
      </div>
      
      {/* תוכן */}
      <div className="flex-grow text-right">
        <div className="flex items-center gap-2 mb-4">
          <NewspaperIcon className="w-6 h-6 text-blue-600" />
          <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            נכתב עלינו ב־TheMarker
          </h3>
        </div>

        <p className="text-gray-700 text-lg leading-relaxed mb-6">
          משרדנו סוקר לאחרונה בכתבה מקיפה על ההשפעה וההצלחות בתחום ההוצאה לפועל,
          תוך הדגשת הגישה החדשנית והייצוג המקצועי שאנו מעניקים ללקוחותינו.
        </p>

        <a
          href="https://www.themarker.com/labels/lawyers-team/2025-06-24/ty-article-labels/00000197-a075-d02a-a1df-a3ff6bfa0001"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-xl shadow-lg transition-transform transform hover:scale-105"
        >
          קראו את הכתבה המלאה
        </a>
      </div>
    </div>
  </div>
)}

        <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Sub Topics Section - רק אם זה לא ייפוי כוח מתמשך */}
        {!isPowerOfAttorney && topicData.subSubjects && topicData.subSubjects.length > 0 && (
          <SubTopicsGrid subSubjects={topicData.subSubjects} />
        )}
        
        {isPowerOfAttorney ? <PowerOfAttorneySteps /> : <ProcessSteps />}
      </div>
        {(() => {
    if (isMobile) {
      // במובייל - מציג את כל התוכן יחד בלי חלוקה
      return (
        <div className="text-left sm:text-center p-4 sm:p-14">
          <div className="sm:text-lg">
            <ContentRenderer content={topicData.generalContent} />
          </div>
        </div>
      );
    }

          // בדסקטופ - מחלק לשני חלקים עם תמונות
          const [firstHalf, secondHalf] = splitContent(topicData.generalContent);
          return (
            <div className="space-y-0 md:space-y-16">
              {/* חלק ראשון - תמונה בצד ימין */}
              <SectionWithImage
                imageSrc={getTopicSecondImage(topicData)}
                imagePosition="right"
                imageAlt={`תמונה עבור נושא ${topicData.name} - חלק ראשון`}
              >
                <div className="text-left sm:text-center p-4 sm:p-14">
                  <div className="sm:text-lg">
                    <ContentRenderer content={firstHalf} />
                  </div>
                </div>
              </SectionWithImage>

              {/* חלק שני - תמונה בצד שמאל */}
              <SectionWithImage
                imageSrc={getTopicThirdImage(topicData)}
                imagePosition="left"
                imageAlt={`תמונה עבור נושא ${topicData.name} - חלק שני`}
              >
                <div className="text-left sm:text-center p-4 sm:p-14">
                  <div className="sm:text-lg">
                    <ContentRenderer content={secondHalf} />
                  </div>
                </div>
              </SectionWithImage>
            </div>
          );
        })()}
 
        <div className="text-center pt-12 pb-0 mb-6">
          <div className="mt-8 bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              צריכים עזרה בנושא {topicData.name}?
            </h3>
            <p className="text-gray-600 mb-4">
              השאירו פרטים לייעוץ ראשוני ללא התחייבות
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl group gap-2"
            >
              <span>השאירו פרטים לייעוץ ראשוני</span>
              <ArrowLeft className="w-5 h-5 transform transition-transform group-hover:-translate-x-1" />
            </Link>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>ללא עלות</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-blue-500" />
                <span>מענה מהיר</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-purple-500" />
                <span>דיסקרטיות מלאה</span>
              </div>
            </div>
          </div>
        </div>
        
   {/* Articles Section - רק אם יש מאמרים */}
{articles.length > 0 && (
  <div className="px-6 md:px-8 lg:px-12">
    <div className="flex items-center mb-6 p-10">
      <NewspaperIcon className="w-6 h-6 text-[#64748b] ml-2" />
      <h2 className="text-2xl font-semibold text-gray-900">מאמרים בנושא</h2>
    </div>
    
    <div className="relative">
      <div className="flex overflow-x-auto gap-6 pb-4 md:grid md:grid-cols-3 snap-x snap-mandatory hide-scrollbar">
        {articles.map(article => (
          <div key={article.id} className="w-[280px] md:w-auto flex-shrink-0 snap-center">
            <ArticleCard
              title={article.title}
              content={article.content}
              articleId={article.id}
              image={article.imageUrl}
            />
          </div>
        ))}
      </div>
    </div>
  </div>
)}
{/* {isExecution && <ArticleShowcase />} */}
<ReasonsList/>

{/* Questions Section - רק אם יש שאלות */}
{questions.length > 0 && (
  <div className="mb-12 px-6 md:px-8 lg:px-12">
    <div className="flex items-center mb-6">
      <QuestionMarkCircleIcon className="w-6 h-6 text-[#64748b] ml-2" />
      <h2 className="text-2xl font-semibold text-gray-900">שאלות נפוצות שחשוב שתדעו:</h2>
    </div>
    <div className="space-y-4">
      {questions.map(q => (
        <QuestionCard
          key={q.id}
          question={q.question}
          answer={q.answer}
          isActive={activeQuestion === q.id}
          onClick={() => setActiveQuestion(activeQuestion === q.id ? null : q.id)}
        />
      ))}
    </div>
  </div>
)}
     
        {/* Reviews Section */}
        <ReviewsSlider
          subjectId={topicIdMapping[topicId]}
          showTitle={true}
          title={`המלצות בתחום ${topicData.name}`}
          maxReviews={50}
          showControllers={true}
        />

        {/* Header Section with CTA Box */}
        <div className="text-center py-5">
          <Link 
            to="/contact" 
            className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl group gap-2"
          >
            <span>השאירו פרטים לייעוץ ראשוני</span>
            <ArrowLeft className="w-5 h-5 transform transition-transform group-hover:-translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TopicPage;